// @oasis-permission-inherits: /jerboa (jerboa:view)
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  RenderingFilterBar,
  type RenderingFilters,
} from "~/modules/jerboa/components/rendering-filter-bar";
import { RenderingMetricsSummary } from "~/modules/jerboa/components/rendering-metrics-summary";
import { RenderingStatusBadge } from "~/modules/jerboa/components/rendering-status-badge";
import {
  fetchJerboaRenderingAnalysisQuery,
  fetchJerboaRenderingResultsQuery,
} from "~/modules/jerboa/queries";

const searchSchema = z.object({
  page: z.number().optional(),
  status: z.enum(["SUCCESS", "RENDER_FAILED"]).optional(),
  projectType: z.enum(["captions-only", "ai-edit"]).optional(),
});

export const Route = createFileRoute("/_dash/jerboa/rendering-results/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleString();
}

function formatDuration(seconds: number | null | undefined) {
  if (seconds == null) {
    return "-";
  }
  return `${seconds.toFixed(1)}s`;
}

function RouteComponent() {
  const { page, status, projectType } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const router = useRouter();

  const filters: RenderingFilters = useMemo(() => ({ status, projectType }), [status, projectType]);

  const onFiltersChange = (newFilters: RenderingFilters) => {
    navigate({
      search: {
        page: 1,
        ...newFilters,
      },
    });
  };

  const {
    data: analysisData,
    isPending: analysisPending,
    error: analysisError,
    refetch: refetchAnalysis,
    isFetching: analysisFetching,
  } = fetchJerboaRenderingAnalysisQuery.useQuery({
    params: { days: 7 },
  });

  const buckets = analysisData?.analysis.buckets ?? [];

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaRenderingResultsQuery.useInfiniteQuery({
      params: filters,
    });

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...filters, page: nextPage } }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Server exports</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Production rendering health and individual export results.
          </p>
        </div>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <section aria-labelledby="rendering-analysis-heading" className="space-y-4">
        <div>
          <h2 id="rendering-analysis-heading" className="text-lg font-semibold">
            Rendering health
          </h2>
          <p className="text-sm text-muted-foreground">
            Last 7 days · All server exports. Result filters below do not affect these charts.
          </p>
        </div>
        {analysisError && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-muted p-4 text-sm text-warning-foreground"
          >
            <p>
              {analysisData
                ? "Could not refresh rendering health. Showing the last available data."
                : "Could not load rendering health."}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={analysisFetching}
              onClick={() => void refetchAnalysis()}
            >
              {analysisFetching ? "Retrying…" : "Retry analysis"}
            </Button>
          </div>
        )}
        {analysisPending && (
          <div role="status" aria-label="Loading rendering health" className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-24" />
              ))}
            </div>
            <Skeleton className="h-96" />
          </div>
        )}
        {buckets.length > 0 && <RenderingMetricsSummary buckets={buckets} />}
        {!analysisPending && !analysisError && buckets.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No rendering health data in the last 7 days.
          </div>
        )}
      </section>

      <div className="space-y-3 rounded-lg border bg-card p-4 text-card-foreground">
        <h2 className="text-lg font-semibold">Export results</h2>
        <RenderingFilterBar filters={filters} onFiltersChange={onFiltersChange} />
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load rendering results</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && !error && !currentPageData && data && (
        <div className="text-sm text-muted-foreground">
          Data loaded ({data.pages.length} pages) but current page ({currentPageIndex}) has no data.
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Project Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Render Duration</TableHead>
              <TableHead className="text-right">Peak Memory</TableHead>
              <TableHead>Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.results.page.map((result) => (
              <TableRow
                key={result.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() =>
                  router.navigate({
                    to: "/jerboa/rendering-results/$resultId",
                    params: { resultId: result.id },
                    search: { page: currentPageIndex + 1, ...filters },
                  })
                }
              >
                <TableCell>
                  <Link
                    to="/jerboa/rendering-results/$resultId"
                    params={{ resultId: result.id }}
                    search={{ page: currentPageIndex + 1, ...filters }}
                    className="rounded-sm text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {formatDate(result.createdAt)}
                  </Link>
                </TableCell>
                <TableCell>
                  {result.projectType ? (
                    <Badge variant="outline" className="text-xs">
                      {result.projectType}
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <RenderingStatusBadge status={result.status} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatDuration(result.renderStats?.renderSeconds)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {result.renderStats?.peakMemoryBytes != null
                    ? `${(result.renderStats.peakMemoryBytes / (1024 * 1024)).toFixed(0)} MB`
                    : "-"}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {result.gpuInfo?.version ? (
                    <a
                      href={`https://github.com/bungeeapp/jerboa/commit/${result.gpuInfo.version}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {result.gpuInfo.version.slice(0, 7)}
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {currentPageData.results.page.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No rendering results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
